import { auth, db } from "../../../lib/firebase";
import {
  addDoc,
  collection,
  deleteField,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { clean } from "./dispatchFormatters";
import {
  isAmbulance,
  isCourtesyRecommendedUnit,
  isCourtesyUnit,
} from "./dispatchUnitUtils";
import { nextTimelinePatch } from "./dispatchCallUtils";

function uniqueIds(ids = []) {
  return Array.from(new Set(ids.filter(Boolean)));
}

function sortIds(ids = []) {
  return [...uniqueIds(ids)].sort();
}

function isSameUnitSet(a = [], b = []) {
  return JSON.stringify(sortIds(a)) === JSON.stringify(sortIds(b));
}

function getUnitById(units, unitId) {
  return units.find((u) => u.id === unitId) || null;
}

function isHQCourtesyFlow(call) {
  return !!call?.hq?.courtesyRequested;
}

function isStandaloneCourtesyCall(call) {
  return call?.callType === "courtesyTransport";
}

function isCourtesyFlow(call) {
  return isStandaloneCourtesyCall(call) || isHQCourtesyFlow(call);
}

function isTransportCompletionUnit(call, unitId) {
  if (call.courtesyTransportUnitId && call.courtesyTransportUnitId === unitId) return true;
  if (call.ambulanceUnitId && call.ambulanceUnitId === unitId) return true;
  if (call.hospitalTransport?.ambulanceUnitId && call.hospitalTransport.ambulanceUnitId === unitId) {
    return true;
  }
  return false;
}

function getRemainingAssignedIds(call, removedUnitId) {
  return (call.assignedUnitIds || []).filter((id) => id !== removedUnitId);
}

function getRemainingStatusIds(call, removedUnitId) {
  return Object.keys(call.unitStatuses || {}).filter((id) => id !== removedUnitId);
}

function hasAnyUnitsRemaining(call, removedUnitId) {
  return (
    getRemainingAssignedIds(call, removedUnitId).length > 0 ||
    getRemainingStatusIds(call, removedUnitId).length > 0
  );
}

function getReplacementCourtesyUnitId(call, removedUnitId, units) {
  const remainingAssigned = getRemainingAssignedIds(call, removedUnitId);
  for (const unitId of remainingAssigned) {
    const unit = getUnitById(units, unitId);
    if (isCourtesyUnit(unit)) return unitId;
  }

  const remainingStatuses = getRemainingStatusIds(call, removedUnitId);
  for (const unitId of remainingStatuses) {
    const unit = getUnitById(units, unitId);
    if (isCourtesyUnit(unit)) return unitId;
  }

  return null;
}

function getAllUnitIdsForCall(call, units) {
  const fromCall = [
    ...(call.assignedUnitIds || []),
    ...Object.keys(call.unitStatuses || {}),
    ...(call.clearedUnitIds || []),
  ];

  const fromUnits = (units || [])
    .filter((u) => u.currentCallId === call.id)
    .map((u) => u.id);

  return uniqueIds([...fromCall, ...fromUnits]);
}

export function useDispatchActions({
  units,
  availableUnits,
  suggested,
  formState,
  setBusy,
  resetForm,
}) {
  const {
    callType,
    location,
    nature,
    notes,
    pickupLocation,
    dropoffLocation,
    passengerCount,
    selectedUnitIds,
    editingQueuedCallId,
  } = formState;

  async function createOrUpdateCall(sendNow) {
    const courtesyMode = callType === "courtesyTransport";

    if (courtesyMode) {
      if (!clean(pickupLocation) || !clean(dropoffLocation)) {
        alert("Enter a pick up and drop off location.");
        return;
      }
    } else if (!location || !nature) {
      alert("Select a location and nature.");
      return;
    }

    setBusy(true);
    try {
      const assignedUnitIds = sendNow ? uniqueIds(selectedUnitIds) : [];
      const courtesyRecommendedIds = courtesyMode
        ? availableUnits.filter(isCourtesyRecommendedUnit).map((u) => u.id)
        : [];
      const effectiveRecommendedIds = courtesyMode
        ? courtesyRecommendedIds
        : suggested.autoSelectedIds || [];
      const suggestedSupervisorIds = courtesyMode ? [] : suggested.suggestedOnlyIds || [];
      const createdBy = auth.currentUser?.uid || null;
      const destinationType = courtesyMode ? "courtesy" : "HQ";

      const pickup = courtesyMode ? clean(pickupLocation) : clean(location);
      const dropoff = courtesyMode ? clean(dropoffLocation) : "Medical HQ";
      const normalizedPassengerCount =
        courtesyMode && Number(passengerCount) > 0 ? Number(passengerCount) : null;

      const sendAsQueued = !sendNow;

      const unitStatuses = {};
      const unitTimelines = {};
      assignedUnitIds.forEach((unitId) => {
        unitStatuses[unitId] = "DISP";
        unitTimelines[unitId] = { dispatchedAt: serverTimestamp() };
      });

      const courtesyTransportUnitId = courtesyMode
        ? assignedUnitIds.find((id) => {
            const unit = getUnitById(units, id);
            return isCourtesyUnit(unit);
          }) || null
        : null;

      const basePayload = {
        location: courtesyMode ? null : clean(location),
        nature: courtesyMode ? null : clean(nature),
        notes: clean(notes),
        callType,
        status: sendAsQueued ? "queued" : "dispatched",
        queuedAt: sendAsQueued ? serverTimestamp() : null,
        dispatchedAt: sendAsQueued ? null : serverTimestamp(),
        recommendedUnitIds: effectiveRecommendedIds,
        suggestedSupervisorIds,
        assignedUnitIds,
        manualOverride: !isSameUnitSet(assignedUnitIds, effectiveRecommendedIds),
        destinationType,
        destinationName: courtesyMode ? dropoff || null : "Medical HQ",
        pickupLocation: pickup || null,
        dropoffLocation: courtesyMode ? dropoff || null : "Medical HQ",
        passengerCount: normalizedPassengerCount,
        courtesyTransportUnitId,
      };

      if (editingQueuedCallId) {
        await updateDoc(doc(db, "dispatchCalls", editingQueuedCallId), {
          ...basePayload,
          ...(sendNow ? { unitStatuses, unitTimelines } : {}),
          ...(courtesyMode
            ? {
                "hq.courtesyRequested": false,
                "hq.courtesyAcknowledged": false,
                "hq.courtesyQueued": false,
                "hq.courtesyDestination": null,
                "hq.courtesyPickupLocation": null,
                "hq.courtesyPassengerCount": null,
              }
            : {}),
        });

        if (sendNow) {
          for (const unitId of assignedUnitIds) {
            await updateDoc(doc(db, "dispatchUnits", unitId), {
              status: "DISP",
              currentCallId: editingQueuedCallId,
              location: pickup || null,
              lastStatusChangeAt: serverTimestamp(),
            });
          }
        }

        resetForm();
        return;
      }

      const ref = await addDoc(collection(db, "dispatchCalls"), {
        ...basePayload,
        createdAt: serverTimestamp(),
        createdBy,
        onSceneAt: null,
        transportingAt: null,
        arrivedDestinationAt: null,
        availableAgainAt: null,
        completedAt: null,
        cancelledAt: null,
        clearedUnitIds: [],
        patientAtHQ: false,
        hq: {
          firstName: null,
          lastInitial: null,
          bedNumber: null,
          chiefComplaint: null,
          consentSigned: false,
          incidentReportCompleted: false,
          pcrCompleted: false,
          discharged: false,
          dischargeDisposition: null,
          courtesyRequested: false,
          courtesyAcknowledged: false,
          courtesyQueued: false,
          courtesyDestination: null,
          courtesyPickupLocation: null,
          courtesyPassengerCount: null,
        },
        hospitalTransport: {
          requested: false,
          acknowledged: false,
          destinationHospital: null,
          ambulanceUnitId: null,
          requestedAt: null,
          dispatchedAt: null,
          transportingAt: null,
          availableAt: null,
        },
        ambulanceUnitId: null,
        unitStatuses,
        unitTimelines,
      });

      if (sendNow) {
        for (const unitId of assignedUnitIds) {
          await updateDoc(doc(db, "dispatchUnits", unitId), {
            status: "DISP",
            currentCallId: ref.id,
            location: pickup || null,
            lastStatusChangeAt: serverTimestamp(),
          });
        }
      }

      resetForm();
    } catch (err) {
      console.error("Create/update call failed:", err);
      alert(`Call failed: ${err.message}`);
    } finally {
      setBusy(false);
    }
  }

  async function cancelCall(call) {
    try {
      const allUnitIds = getAllUnitIdsForCall(call, units);

      await updateDoc(doc(db, "dispatchCalls", call.id), {
        status: "cancelled",
        cancelledAt: serverTimestamp(),
        assignedUnitIds: [],
        unitStatuses: {},
        courtesyTransportUnitId: null,
        ambulanceUnitId: null,
        "hospitalTransport.ambulanceUnitId": null,
      });

      for (const unitId of allUnitIds) {
        await updateDoc(doc(db, "dispatchUnits", unitId), {
          status: "AVAIL",
          currentCallId: null,
          location: null,
          lastStatusChangeAt: serverTimestamp(),
        });
      }

      resetForm();
    } catch (err) {
      console.error("Cancel call failed:", err);
      alert(`Cancel failed: ${err.message}`);
    }
  }

  async function completeCall(call, outcome = "completed") {
    try {
      const allUnitIds = getAllUnitIdsForCall(call, units);

      await updateDoc(doc(db, "dispatchCalls", call.id), {
        status: "completed",
        outcome,
        completedAt: serverTimestamp(),
        availableAgainAt: serverTimestamp(),
        assignedUnitIds: [],
        unitStatuses: {},
        courtesyTransportUnitId: null,
        ambulanceUnitId: null,
        "hospitalTransport.ambulanceUnitId": null,
      });

      for (const unitId of allUnitIds) {
        await updateDoc(doc(db, "dispatchUnits", unitId), {
          status: "AVAIL",
          currentCallId: null,
          location: null,
          lastStatusChangeAt: serverTimestamp(),
        });
      }
    } catch (err) {
      console.error("Complete call failed:", err);
      alert(`Complete failed: ${err.message}`);
    }
  }

async function getLinkedCourtesyTrips(call) {
  const tripGroupId = call.tripGroupId || call.id;

  const snap = await getDocs(
    query(collection(db, "dispatchCalls"), where("tripGroupId", "==", tripGroupId))
  );

  const linked = snap.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  }));

  if (!linked.some((c) => c.id === call.id)) {
    linked.push(call);
  }

  return linked.filter((c) => c.callType === "courtesyTransport");
}

  async function resolveInField(call) {
    await completeCall(call, "resolvedInField");
  }

  async function assignSpecificUnitToCall(call, unit) {
    const nextAssigned = uniqueIds([...(call.assignedUnitIds || []), unit.id]);
    const pickup =
      clean(call.pickupLocation) ||
      clean(call.hq?.courtesyPickupLocation) ||
      clean(call.location) ||
      null;

    const patch = {
      assignedUnitIds: nextAssigned,
      status: call.status === "queued" ? "dispatched" : call.status,
      queuedAt: call.status === "queued" ? call.queuedAt || serverTimestamp() : call.queuedAt || null,
      dispatchedAt: call.dispatchedAt || serverTimestamp(),
      [`unitStatuses.${unit.id}`]: "DISP",
      [`unitTimelines.${unit.id}.dispatchedAt`]: serverTimestamp(),
    };

    if (isAmbulance(unit)) {
      patch.ambulanceUnitId = unit.id;
      patch["hospitalTransport.requested"] = true;
      patch["hospitalTransport.acknowledged"] = true;
      patch["hospitalTransport.ambulanceUnitId"] = unit.id;
      patch["hospitalTransport.dispatchedAt"] = serverTimestamp();
    }

      if (isCourtesyFlow(call)) {
      patch.courtesyTransportUnitId = unit.id;

      patch["hq.courtesyAcknowledged"] = true;
      patch["hq.courtesyQueued"] = false;
      patch["hq.courtesyRequested"] = isStandaloneCourtesyCall(call)
        ? call.hq?.courtesyRequested || false
        : true;
    }

    await updateDoc(doc(db, "dispatchCalls", call.id), patch);

    await updateDoc(doc(db, "dispatchUnits", unit.id), {
      status: "DISP",
      currentCallId: call.id,
      location: pickup,
      lastStatusChangeAt: serverTimestamp(),
    });

    if (isStandaloneCourtesyCall(call) && call.sourceCallId) {
  await updateDoc(doc(db, "dispatchCalls", call.sourceCallId), {
    courtesyTransportUnitId: unit.id,
    [`unitStatuses.${unit.id}`]: "DISP",
    status: "dispatched",
    "hq.courtesyAcknowledged": true,
    "hq.courtesyQueued": false,
  });
  }
}
  

  async function acknowledgeAmbulanceRequest(call) {
    try {
      const recommendedAmbulance =
        availableUnits.find((u) => isAmbulance(u)) || null;

      await updateDoc(doc(db, "dispatchCalls", call.id), {
        "hospitalTransport.acknowledged": true,
        "hospitalTransport.requested": true,
        recommendedUnitIds: recommendedAmbulance ? [recommendedAmbulance.id] : [],
      });
    } catch (err) {
      console.error("Acknowledge ambulance failed:", err);
      alert(`Ambulance acknowledge failed: ${err.message}`);
    }
  }

  async function acknowledgeCourtesyRequest(call) {
    try {
     const recommendedTransport =
  availableUnits.find((u) => isCourtesyRecommendedUnit(u)) ||
  units.find((u) => isCourtesyRecommendedUnit(u)) ||
  null;
      await updateDoc(doc(db, "dispatchCalls", call.id), {
        "hq.courtesyRequested": true,
        "hq.courtesyAcknowledged": true,
        "hq.courtesyQueued": !recommendedTransport,
        "hq.courtesyPickupLocation":
          clean(call.hq?.courtesyPickupLocation) || "Medical HQ",
        recommendedUnitIds: recommendedTransport ? [recommendedTransport.id] : [],
      });
    } catch (err) {
      console.error("Acknowledge courtesy failed:", err);
      alert(`Courtesy acknowledge failed: ${err.message}`);
    }
  }


  async function updateUnitOnCall(call, unitId, nextStatus) {
  try {
    if (unitId === "__editNotes") {
      await updateDoc(doc(db, "dispatchCalls", call.id), {
        notes: nextStatus,
      });
      return;
    }

    if (unitId === "__editPickup") {
      await updateDoc(doc(db, "dispatchCalls", call.id), {
        pickupLocation: nextStatus,
      });
      return;
    }

    if (unitId === "__editDropoff") {
      await updateDoc(doc(db, "dispatchCalls", call.id), {
        dropoffLocation: nextStatus,
      });
      return;
    }

    const unit = getUnitById(units, unitId);
    if (!unit) return;

    const shouldSyncLinkedCourtesyStatus =
      isStandaloneCourtesyCall(call) &&
      ["DISP", "ON_SCENE", "TRANSPORTING"].includes(nextStatus);

    // leave the rest of your existing function exactly as it is below this line

    if (shouldSyncLinkedCourtesyStatus) {
      const linkedCalls = await getLinkedCourtesyTrips(call);

      for (const linkedCall of linkedCalls) {
        const linkedPatch = {
          [`unitStatuses.${unitId}`]: nextStatus,
          ...nextTimelinePatch(unitId, nextStatus, serverTimestamp),
        };

        if (nextStatus === "DISP") {
          linkedPatch.status =
            linkedCall.status === "queued" ? "dispatched" : linkedCall.status || "dispatched";
          if (!linkedCall.dispatchedAt) {
            linkedPatch.dispatchedAt = serverTimestamp();
          }
        }

        if (nextStatus === "ON_SCENE") {
          if (!linkedCall.onSceneAt) linkedPatch.onSceneAt = serverTimestamp();
          linkedPatch.status = "onScene";
        }

        if (nextStatus === "TRANSPORTING") {
  if (!linkedCall.transportingAt) {
    linkedPatch.transportingAt = serverTimestamp();
  }

  linkedPatch.status = "transporting";

  // keep visible on HQ tracker
  linkedPatch.patientAtHQ = true;

  // remove from bed
  linkedPatch["hq.bedNumber"] = null;

  // HQ label
  linkedPatch["hq.dischargeDisposition"] = "PT Discharged";
  linkedPatch["hq.discharged"] = true;
}

        await updateDoc(doc(db, "dispatchCalls", linkedCall.id), linkedPatch);
      }

      await updateDoc(doc(db, "dispatchUnits", unitId), {
  status: nextStatus,
  currentCallId: call.id,
  location:
    clean(call.pickupLocation) ||
    clean(call.hq?.courtesyPickupLocation) ||
    clean(call.location) ||
    null,
  lastStatusChangeAt: serverTimestamp(),
});

// Sync the original HQ patient card
if (call.sourceCallId) {
  const originalCallPatch = {
    courtesyTransportUnitId: unitId,
    [`unitStatuses.${unitId}`]: nextStatus,
  };

  if (nextStatus === "DISP") {
  originalCallPatch.status = "dispatched";
}

if (nextStatus === "ON_SCENE") {
  originalCallPatch.status = "onScene";
}

if (nextStatus === "TRANSPORTING") {
  originalCallPatch.status = "transporting";
  originalCallPatch.patientAtHQ = true;
}

  if (nextStatus === "DISP") {
    originalCallPatch["hq.courtesyAcknowledged"] = true;
    originalCallPatch["hq.courtesyQueued"] = false;
  }

  if (nextStatus === "ON_SCENE") {
    originalCallPatch["hq.courtesyAcknowledged"] = true;
    originalCallPatch["hq.courtesyQueued"] = false;
  }

  if (nextStatus === "TRANSPORTING") {
    originalCallPatch["hq.bedNumber"] = null;
    originalCallPatch["hq.dischargeDisposition"] = "PT Discharged";
    originalCallPatch["hq.discharged"] = true;
    originalCallPatch.patientAtHQ = true;
  }

  await updateDoc(
    doc(db, "dispatchCalls", call.sourceCallId),
    originalCallPatch
  );
}

return;
    }

    const callPatch = {
      [`unitStatuses.${unitId}`]: nextStatus,
      ...nextTimelinePatch(unitId, nextStatus, serverTimestamp),
    };

    if (nextStatus === "ON_SCENE") {
      if (!call.onSceneAt) callPatch.onSceneAt = serverTimestamp();
      if (call.status === "dispatched") callPatch.status = "onScene";
    }

    if (["TO_HQ", "TRANSPORTING"].includes(nextStatus)) {
      if (!call.transportingAt) callPatch.transportingAt = serverTimestamp();
      callPatch.status = "transporting";
    }

    if (["ARRIVED_HQ", "ARRIVED"].includes(nextStatus)) {
      if (!call.arrivedDestinationAt) callPatch.arrivedDestinationAt = serverTimestamp();

      if (nextStatus === "ARRIVED_HQ") {
        callPatch.status = "arrivedHQ";
        callPatch.patientAtHQ = true;
      } else {
        callPatch.status = "arrivedDestination";
      }
    }

    if (isAmbulance(unit)) {
      if (nextStatus === "TRANSPORTING") {
  if (!call.hospitalTransport?.transportingAt) {
    callPatch["hospitalTransport.transportingAt"] = serverTimestamp();
  }

  callPatch["hospitalTransport.ambulanceUnitId"] = unitId;
  callPatch.ambulanceUnitId = unitId;

  // move patient out of bed but keep them visible in HQ waiting room
  callPatch.patientAtHQ = true;
  callPatch["hq.bedNumber"] = null;

  // label for HQ
  callPatch["hq.dischargeDisposition"] = "PT Transported to Hosp";
  callPatch["hq.discharged"] = true;
}

       if (nextStatus === "ARRIVED") {
  if (!call.hospitalTransport?.arrivedAt) {
    callPatch["hospitalTransport.arrivedAt"] = serverTimestamp();
  }
}

      if (nextStatus === "AVAIL") {
        callPatch["hospitalTransport.availableAt"] = serverTimestamp();
        callPatch["hospitalTransport.ambulanceUnitId"] = unitId;
        callPatch.ambulanceUnitId = unitId;
      }
    }

    if (isCourtesyFlow(call)) {
      if (nextStatus === "DISP") {
        callPatch["hq.courtesyAcknowledged"] = true;
        callPatch["hq.courtesyQueued"] = false;
      }

     if (nextStatus === "TRANSPORTING") {
  callPatch.patientAtHQ = true;

  // 🔥 NEW LOGIC
  callPatch["hq.bedNumber"] = null;              // removes from bed
  callPatch["hq.dischargeDisposition"] = "PT Discharged";
  callPatch["hq.discharged"] = true;
}
    }

    const removingFromCall = nextStatus === "CLEAR" || nextStatus === "AVAIL";

    if (removingFromCall) {
      const remainingAssignedIds = getRemainingAssignedIds(call, unitId);
      const replacementCourtesyUnitId = getReplacementCourtesyUnitId(call, unitId, units);
      const unitWasTransportUnit = isTransportCompletionUnit(call, unitId);

      callPatch.assignedUnitIds = remainingAssignedIds;
      callPatch.clearedUnitIds = Array.from(new Set([...(call.clearedUnitIds || []), unitId]));
      callPatch[`unitStatuses.${unitId}`] = deleteField();

      if (call.courtesyTransportUnitId === unitId) {
        callPatch.courtesyTransportUnitId = replacementCourtesyUnitId;
      }

      if (call.ambulanceUnitId === unitId) {
        callPatch.ambulanceUnitId = null;
      }

      if (call.hospitalTransport?.ambulanceUnitId === unitId) {
        callPatch["hospitalTransport.ambulanceUnitId"] = null;
      }

      if (unitWasTransportUnit && !hasAnyUnitsRemaining(call, unitId)) {
        callPatch.patientAtHQ = false;
      }
    }

    await updateDoc(doc(db, "dispatchCalls", call.id), callPatch);
    if (isStandaloneCourtesyCall(call) && call.sourceCallId) {
  const originalCallPatch = {};

  if (nextStatus === "DISP") {
    originalCallPatch.status = "dispatched";
    originalCallPatch["hq.courtesyAcknowledged"] = true;
    originalCallPatch["hq.courtesyQueued"] = false;
  }

  if (nextStatus === "ON_SCENE") {
    originalCallPatch.status = "onScene";
    originalCallPatch["hq.courtesyAcknowledged"] = true;
    originalCallPatch["hq.courtesyQueued"] = false;
  }

  if (nextStatus === "TRANSPORTING") {
    originalCallPatch.status = "transporting";
    originalCallPatch.patientAtHQ = true;

    originalCallPatch["hq.bedNumber"] = null;
    originalCallPatch["hq.dischargeDisposition"] = "PT Discharged";
    originalCallPatch["hq.discharged"] = true;
  }

  if (Object.keys(originalCallPatch).length) {
    await updateDoc(
      doc(db, "dispatchCalls", call.sourceCallId),
      originalCallPatch
    );
  }
}

    await updateDoc(doc(db, "dispatchUnits", unitId), {
      status: removingFromCall ? "AVAIL" : nextStatus,
      currentCallId: removingFromCall ? null : call.id,
      location: removingFromCall
        ? null
        : clean(call.pickupLocation) ||
          clean(call.hq?.courtesyPickupLocation) ||
          clean(call.location) ||
          null,
      lastStatusChangeAt: serverTimestamp(),
    });
if (removingFromCall && hasAnyUnitsRemaining(call, unitId)) {
  return;
}

if (removingFromCall && !hasAnyUnitsRemaining(call, unitId)) {
  if (call.callType === "courtesyTransport") {
    await completeCall(call, "courtesyTransportComplete");
    return;
  }

  if (isAmbulance(unit) && nextStatus === "AVAIL") {
    await completeCall(call, "ambulanceTransportComplete");
    return;
  }

  const shouldKeepOnHQTracker =
    call.patientAtHQ === true ||
    call.status === "arrivedHQ" ||
    !!call.hq?.bedNumber;

  if (shouldKeepOnHQTracker) {
    await updateDoc(doc(db, "dispatchCalls", call.id), {
      status: "arrivedHQ",
      patientAtHQ: true,
      availableAgainAt: serverTimestamp(),
    });

    return;
  }

  await completeCall(
    call,
    call.callType === "standby" ? "standbyComplete" : "completed"
  );

  return;
}

 } catch (err) {
  console.error(
    "Update unit status failed",
    {
      callId: call?.id,
      unitId,
      nextStatus,
      error: err,
    }
  );

  alert(
    `Status update failed:\n\n${err.message}\n\nCheck browser console for details.`
  );
}
}

  async function addUnitToCall(call, unitId) {
    if (!unitId) return;
    const unit = getUnitById(units, unitId);
    if (!unit) return;
    await assignSpecificUnitToCall(call, unit);
  }

  async function toggleUnitService(unit) {
    try {
      if (unit.currentCallId) return;
      const nextStatus = unit.status === "OOS" ? "AVAIL" : "OOS";
      await updateDoc(doc(db, "dispatchUnits", unit.id), {
        status: nextStatus,
        lastStatusChangeAt: serverTimestamp(),
      });
    } catch (err) {
      console.error("Toggle unit service failed:", err);
      alert(`Unit update failed: ${err.message}`);
    }
  }

  async function queueCourtesyRequest(call) {
  try {
    await updateDoc(doc(db, "dispatchCalls", call.id), {
      "hq.courtesyRequested": true,
      "hq.courtesyAcknowledged": true,
      "hq.courtesyQueued": true,
      courtesyTransportUnitId: null,
      recommendedUnitIds: [],
    });
  } catch (err) {
    console.error("Queue courtesy failed:", err);
    alert(`Queue failed: ${err.message}`);
  }
}

  return {
    createOrUpdateCall,
    cancelCall,
    resolveInField,
    acknowledgeAmbulanceRequest,
    acknowledgeCourtesyRequest,
    queueCourtesyRequest,
    updateUnitOnCall,
    addUnitToCall,
addTripToCall: async (call) => {
  if (!call || call.callType !== "courtesyTransport") return;

  const tripGroupId = call.tripGroupId || call.id;
  const pickup =
    clean(call.pickupLocation) ||
    clean(call.hq?.courtesyPickupLocation) ||
    clean(call.location) ||
    null;

  const assignedUnitIds = uniqueIds(call.assignedUnitIds || []);
  const courtesyTransportUnitId =
    call.courtesyTransportUnitId ||
    assignedUnitIds.find((id) => {
      const unit = getUnitById(units, id);
      return isCourtesyUnit(unit);
    }) ||
    null;

  const unitStatuses = {};
  const unitTimelines = {};

  assignedUnitIds.forEach((unitId) => {
    unitStatuses[unitId] = call.unitStatuses?.[unitId] || "DISP";
    unitTimelines[unitId] = {
      dispatchedAt: serverTimestamp(),
    };
  });

  await addDoc(collection(db, "dispatchCalls"), {
    location: null,
    nature: null,
    notes: clean(call.notes),
    callType: "courtesyTransport",
    status: call.status === "queued" ? "queued" : "dispatched",
    queuedAt: call.status === "queued" ? serverTimestamp() : null,
    dispatchedAt: call.status === "queued" ? null : serverTimestamp(),
    createdAt: serverTimestamp(),
    createdBy: auth.currentUser?.uid || null,

    tripGroupId,
    recommendedUnitIds: [],
    suggestedSupervisorIds: [],
    assignedUnitIds,
    manualOverride: true,

    destinationType: "courtesy",
    destinationName: null,
    pickupLocation: pickup,
    dropoffLocation: null,
    passengerCount: null,
    courtesyTransportUnitId,

    onSceneAt: null,
    transportingAt: null,
    arrivedDestinationAt: null,
    availableAgainAt: null,
    completedAt: null,
    cancelledAt: null,
    clearedUnitIds: [],
    patientAtHQ: false,

    hq: {
      firstName: null,
      lastInitial: null,
      bedNumber: null,
      chiefComplaint: null,
      consentSigned: false,
      incidentReportCompleted: false,
      pcrCompleted: false,
      discharged: false,
      dischargeDisposition: null,
      courtesyRequested: false,
      courtesyAcknowledged: false,
      courtesyQueued: false,
      courtesyDestination: null,
      courtesyPickupLocation: null,
      courtesyPassengerCount: null,
    },

    hospitalTransport: {
      requested: false,
      acknowledged: false,
      destinationHospital: null,
      ambulanceUnitId: null,
      requestedAt: null,
      dispatchedAt: null,
      transportingAt: null,
      availableAt: null,
    },

    ambulanceUnitId: null,
    unitStatuses,
    unitTimelines,
  });

  if (!call.tripGroupId) {
    await updateDoc(doc(db, "dispatchCalls", call.id), {
      tripGroupId,
    });
  }
},
    toggleUnitService,
    resetForm,
  };
}